/**
 * Where a platen variant size's file comes from, for the person looking at the
 * issue. A size under the platen size rules is never uploaded from an issue:
 * its file is uploaded in Product updates, which only an admin of the org that
 * owns the product can do. So the issue says one of three things:
 *
 *   - `upload`: an admin looking at their OWN org's issue gets the link to
 *     that product in Product updates (same tab, so `ArrowRight`, not the
 *     new-tab `ExternalLink`).
 *   - `askAdmin`: anyone else in that org (coordinator, floor staff, CX, who
 *     cannot upload there and may not even see product sync) is told to ask an
 *     admin. No call to action: a link would land them on a page they cannot
 *     use.
 *   - `askClient`: a 3PL owner looking at a CLIENT's issue at their floor. The
 *     product and its files belong to the client, so the owner gets no link
 *     and no upload; the client's admins were notified by the server with a
 *     link to that product. A client looking at its own issue (at the 3PL's
 *     shared location) is not cross-org, so it gets `upload` or `askAdmin`.
 *
 * Used by the issue card (the guide line and its one button,
 * `PlatenVariantUpdatesLink`) and the resolve dialog's upload form, so both
 * always say the same thing. The who-acts decision is `issueProductRoute`.
 */
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useRole } from '@/hooks/use-role';
import { useClientOrgLabels } from '@/hooks/useClientOrgLabels';
import { productUpdatesHref } from '@/features/product-sync/lib/tabs';

export type PlatenVariantRoute =
  | { kind: 'upload'; href: string }
  | { kind: 'askAdmin' }
  | { kind: 'askClient'; clientName: string };

/**
 * Who acts on an issue's product in its own store, for the person looking at
 * the issue: an admin of the org that owns it (`admin`), anyone else in that
 * org (`askAdmin`), or a 3PL owner looking at a CLIENT's issue (`askClient`).
 * The one routing rule behind the platen variant route (above) and the Fix
 * design data variant set-up step (BLU-1287). Pure: the caller passes values
 * it already holds, so it adds no request.
 */
export type IssueProductRoute =
  | { kind: 'admin' }
  | { kind: 'askAdmin' }
  | { kind: 'askClient'; clientName: string };

export function issueProductRoute(crossOrg: boolean, clientName: string, isAdmin: boolean): IssueProductRoute {
  if (crossOrg) return { kind: 'askClient', clientName };
  if (!isAdmin) return { kind: 'askAdmin' };
  return { kind: 'admin' };
}

/**
 * The route for one issue. `issueOrganizationId` is the issue row's org (the
 * product's owner); `shopifyProductId` opens that product in Product updates.
 */
export function usePlatenVariantRoute(
  issueOrganizationId: string | null | undefined,
  shopifyProductId: number | string | null | undefined,
): PlatenVariantRoute {
  const { t } = useTranslation('production');
  const { isAdmin } = useRole();
  const { isCrossOrg, labelFor } = useClientOrgLabels();
  const route = issueProductRoute(
    isCrossOrg(issueOrganizationId),
    labelFor(issueOrganizationId) ?? t('issues.printFiles.platenVariant.theClient'),
    isAdmin(),
  );
  return route.kind === 'admin' ? { kind: 'upload', href: productUpdatesHref(shopifyProductId) } : route;
}

/** The one sentence for a route (the resolve dialog's upload form). */
export function platenVariantRouteMessage(t: TFunction, route: PlatenVariantRoute): string {
  switch (route.kind) {
    case 'upload':
      return t('issues.printFiles.platenVariant.uploadFromUpdates');
    case 'askAdmin':
      return t('issues.printFiles.platenVariant.askAdmin');
    case 'askClient':
      return t('issues.printFiles.platenVariant.askClient', { client: route.clientName });
  }
}
